import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showToast } from './Toast';
import { dmrApi, type DmrActionItem, type DmrItemInput } from '../api/dmr';

/**
 * DMR action items, in three modes:
 *   - 'edit':       marketing on the call page — full edit, add, delete;
 *   - 'list':       the cross-partner list (marketing changes status, FBCs read);
 *   - 'franchisee': their own visible items; they tick off the ones they own.
 */

const STATUS: Record<DmrActionItem['status'], { text: string; bg: string; fg: string }> = {
  open: { text: 'Open', bg: '#fef3c7', fg: '#92400e' },
  in_progress: { text: 'In progress', bg: '#e0e7ff', fg: '#3730a3' },
  done: { text: 'Done', bg: '#dcfce7', fg: '#15803d' },
  wont_do: { text: 'Won’t do', bg: '#f1f5f9', fg: '#64748b' },
};

const MUTED = '#64748b';

function errText(e: unknown) {
  return e instanceof Error ? e.message : 'Something went wrong';
}

export function useItemMutations(onChanged?: () => void) {
  const qc = useQueryClient();
  const done = () => {
    qc.invalidateQueries({ queryKey: ['dmr'] });
    onChanged?.();
  };
  const onError = (e: unknown) => showToast(errText(e), { variant: 'error', durationMs: 5000 });
  return {
    update: useMutation({ mutationFn: ({ id, body }: { id: number; body: DmrItemInput }) => dmrApi.updateItem(id, body), onSuccess: done, onError }),
    remove: useMutation({ mutationFn: (id: number) => dmrApi.deleteItem(id), onSuccess: done, onError }),
  };
}

export default function DmrActionItems({
  items,
  mode,
  canEdit = false,
  showPartner = false,
}: {
  items: DmrActionItem[];
  mode: 'edit' | 'list' | 'franchisee';
  canEdit?: boolean;
  showPartner?: boolean;
}) {
  const { update, remove } = useItemMutations();
  const [editing, setEditing] = useState<number | null>(null);

  if (items.length === 0) return <p style={{ color: MUTED, fontSize: 14, margin: '4px 0' }}>No action items yet.</p>;

  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
      {items.map((i) => {
        if (editing === i.id) {
          return (
            <li key={i.id}>
              <ItemForm
                initial={i}
                submitLabel="Save"
                onCancel={() => setEditing(null)}
                onSubmit={(body) => update.mutate({ id: i.id, body }, { onSuccess: () => setEditing(null) })}
              />
            </li>
          );
        }
        const s = STATUS[i.status];
        const franchiseeCanTick = mode === 'franchisee' && i.ownerSide === 'franchisee';
        const closed = i.status === 'done' || i.status === 'wont_do';
        return (
          <li key={i.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '9px 12px', background: '#fff' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              {(franchiseeCanTick || (canEdit && mode !== 'franchisee')) && (
                <input
                  type="checkbox"
                  checked={i.status === 'done'}
                  aria-label="Done"
                  style={{ marginTop: 3 }}
                  onChange={(e) => update.mutate({ id: i.id, body: { status: e.target.checked ? 'done' : 'open' } })}
                />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, textDecoration: closed ? 'line-through' : 'none', color: closed ? MUTED : '#1f2a48' }}>
                  {i.title}
                </div>
                {i.detail && <div style={{ fontSize: 13, color: MUTED, whiteSpace: 'pre-wrap' }}>{i.detail}</div>}
                <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
                  {showPartner && i.partnerName && <>{i.partnerName} ({(i.afs ?? []).join(', ')}) · </>}
                  {i.ownerSide === 'amada' ? 'Amada Marketing' : 'Franchise partner'}
                  {i.ownerName && ` (${i.ownerName})`}
                  {i.dueOn && ` · due ${new Date(`${i.dueOn}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`}
                  {mode !== 'franchisee' && !i.visibleToFranchisee && ' · internal only'}
                  {canEdit && mode === 'edit' && (
                    <>
                      <button style={link} onClick={() => setEditing(i.id)}>edit</button>
                      <button style={{ ...link, color: '#b91c1c' }} onClick={() => window.confirm('Delete this action item?') && remove.mutate(i.id)}>
                        delete
                      </button>
                    </>
                  )}
                </div>
              </div>
              {canEdit && mode !== 'franchisee' ? (
                <select
                  value={i.status}
                  aria-label="Status"
                  onChange={(e) => update.mutate({ id: i.id, body: { status: e.target.value as DmrActionItem['status'] } })}
                  style={{ fontSize: 12, padding: '3px 4px', background: s.bg, color: s.fg, fontWeight: 700, border: '1px solid #e2e8f0', borderRadius: 6 }}
                >
                  {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.text}</option>)}
                </select>
              ) : (
                <span style={{ background: s.bg, color: s.fg, borderRadius: 999, padding: '2px 9px', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {s.text}
                </span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function ItemForm({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial?: Partial<DmrActionItem>;
  submitLabel: string;
  onSubmit: (body: DmrItemInput) => void;
  onCancel?: () => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [detail, setDetail] = useState(initial?.detail ?? '');
  const [ownerSide, setOwnerSide] = useState<DmrActionItem['ownerSide']>(initial?.ownerSide ?? 'franchisee');
  const [ownerName, setOwnerName] = useState(initial?.ownerName ?? '');
  const [dueOn, setDueOn] = useState(initial?.dueOn ?? '');
  const [visible, setVisible] = useState(initial?.visibleToFranchisee ?? true);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!title.trim()) return;
        onSubmit({
          title: title.trim(),
          detail,
          ownerSide,
          ownerName,
          dueOn: dueOn || null,
          visibleToFranchisee: visible,
          ...(initial?.templateItemId ? { templateItemId: initial.templateItemId } : {}),
        });
      }}
      style={{ border: '1px solid #cbd5e1', borderRadius: 8, padding: 12, background: '#f8fafc', display: 'grid', gap: 8, fontSize: 14 }}
    >
      <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What needs to happen?" style={input} />
      <textarea value={detail ?? ''} onChange={(e) => setDetail(e.target.value)} placeholder="Details (optional)" rows={2} style={input} />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={ownerSide} onChange={(e) => setOwnerSide(e.target.value as DmrActionItem['ownerSide'])} style={{ ...input, width: 'auto' }}>
          <option value="franchisee">Franchise partner does it</option>
          <option value="amada">Amada Marketing does it</option>
        </select>
        <input value={ownerName ?? ''} onChange={(e) => setOwnerName(e.target.value)} placeholder="Who (optional)" style={{ ...input, width: 160 }} />
        <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          Due <input type="date" value={dueOn ?? ''} onChange={(e) => setDueOn(e.target.value)} style={{ ...input, width: 'auto' }} />
        </label>
      </div>
      <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} />
        Show to the franchisee (untick for internal-only follow-ups)
      </label>
      <div>
        <button type="submit" style={btn}>{submitLabel}</button>
        {onCancel && <button type="button" onClick={onCancel} style={link}>cancel</button>}
      </div>
    </form>
  );
}

const input: React.CSSProperties = {
  padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit',
};
const btn: React.CSSProperties = {
  background: '#1f2a48', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer',
};
const link: React.CSSProperties = {
  background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 12, padding: '0 6px', fontWeight: 600,
};
